import { cn } from "@/utils/cn";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  BUTTON_SIZE,
  type ButtonSize,
  type ButtonVariant,
} from "@/design-system/tokens";
import type { ButtonHTMLAttributes, ReactNode } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children?: ReactNode;
  /** Hover hint — rendered with the app tooltip (not native title). */
  tooltip?: ReactNode;
}

const variants: Record<ButtonVariant, string> = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  danger: "btn-danger",
  ghost: "btn-ghost",
};

export function Button({
  variant = "primary",
  size = "md",
  loading,
  className,
  children,
  disabled,
  tooltip,
  title,
  ...props
}: ButtonProps) {
  const hint = tooltip ?? title;
  const button = (
    <button
      className={cn("btn", variants[variant], BUTTON_SIZE[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden
        />
      )}
      {children}
    </button>
  );

  if (hint) {
    return <Tooltip content={hint}>{button}</Tooltip>;
  }
  return button;
}
